import {useMemo} from 'react'
import isNil from 'lodash/isNil.js'

import ButtonLink from '../../../../common/components/Button/ButtonLink.js'
import FormattedRateCost from '../../../../common/components/FormattedRateCost.js'

import EditWeight from './Fields/EditWeight.js'
import EditDimensions from './Fields/EditDimensions.js'
import RequestedShippingMethod from './Fields/RequestedShippingMethod.js'
import ListItem from './Fields/ListItem.js'
import LabelItem from './Fields/LabelItem.js'
import ShipFrom from './Fields/ShipFrom.js'
import PackageType from './Fields/PackageType.js'
import InsuranceItem from './Fields/InsuranceItem.js'
import PreconfiguredPackingList from './Fields/PreconfiguredPackingList.js'
import {toggleExpanded} from '../../orderListActions.js'
import AssignedWarehouse from './Fields/AssignedWarehouse.js'
import {useSelector} from '../../../../store.js'
import {
  labelErrorsSelector,
  labelWarningsSelector,
  makeBoxShapeNameSelector,
  makeCostSelector,
  makeLabelShipperNameSelector,
  labelShipperTypeSelector,
  labelShippingMethodNameSelector,
} from '../../../../data/labelInfos/index.js'
import {shipperCurrencySymbolSelector} from '../../../../data/shippers.js'
import LabelConfigContext from '../../../LabelConfig/LabelConfigContext.js'
import PackagingWeight from '../../../LabelConfig/Fields/PackagingWeight.js'
import Icon from '../../../../common/icons/Icon.js'

export default function NoShippingLabel({
  orderNumber,
  labelInfoID,
  requestedShippingMethod,
  isCancelled,
  isExpanded,
  parentView,
  showShippingLabelTab,
}) {
  const labelShipperNameSelector = useMemo(
    () => makeLabelShipperNameSelector(),
    [],
  )
  const boxShapeSelector = useMemo(() => makeBoxShapeNameSelector(), [])
  const costSelector = useMemo(() => makeCostSelector(), [])

  const shipperType = useSelector((state) =>
    labelShipperTypeSelector(state, {labelInfoID}),
  )

  const hasLabelInfo = !!labelInfoID

  const shipperName = useSelector((state) =>
    labelShipperNameSelector(state, {labelInfoID}),
  )
  const boxShape = useSelector((state) =>
    hasLabelInfo
      ? boxShapeSelector(state, {labelInfoID, shipperType, packagesIndex: 0})
      : '',
  )
  const cost = useSelector((state) => costSelector(state, {labelInfoID}))
  const shippingMethodName = useSelector((state) =>
    hasLabelInfo ? labelShippingMethodNameSelector(state, {labelInfoID}) : '',
  )
  const shipperCurrencySymbol = useSelector((state) =>
    shipperCurrencySymbolSelector(state, {
      labelInfoID,
    }),
  )
  const shippingLabelErrors = useSelector((state) =>
    hasLabelInfo ? labelErrorsSelector(state, {labelInfoID}) : [],
  )
  const shippingLabelWarnings = useSelector((state) =>
    hasLabelInfo ? labelWarningsSelector(state, {labelInfoID}) : [],
  )

  const hasShippingLabelErrors = shippingLabelErrors.length > 0
  const hasShippingLabelWarnings = shippingLabelWarnings.length > 0
  const errorsAndWarnings = [...shippingLabelErrors, ...shippingLabelWarnings]
  const errorLevel =
    (hasShippingLabelErrors && 'error') ||
    (hasShippingLabelWarnings && 'warning') ||
    ''

  if (!hasLabelInfo) {
    return null
  }

  return (
    <LabelConfigContext.Provider
      value={{labelInfoID, shipperType, isValueSummary: true}}
    >
      <div>
        <div className="flex">
          <div className="flex-grow">
            <h4 className={`list__title--order-data ${errorLevel}`}>
              {hasShippingLabelErrors && (
                <Icon
                  name="ban"
                  className="fs-00 lh-md v-align-middle margin-right-3"
                />
              )}
              {!hasShippingLabelErrors && hasShippingLabelWarnings && (
                <Icon
                  name="exclamation-triangle"
                  className="fs-00 lh-md v-align-middle margin-right-3"
                />
              )}
              <span className="margin-right-5">Shipping Info</span>

              {(hasShippingLabelErrors || hasShippingLabelWarnings) && (
                <button
                  className="btn btn--link v-align-base unbold"
                  type="button"
                  onClick={() => toggleExpanded(orderNumber)}
                >
                  {isExpanded ? 'Hide errors' : 'View errors'}
                </button>
              )}
            </h4>

            {isExpanded &&
              (hasShippingLabelErrors || hasShippingLabelWarnings) && (
                <div
                  className={`panel panel--${errorLevel} panel--sm margin-top-5 margin-bottom-10`}
                >
                  <ul className="list--order-data square">
                    {errorsAndWarnings.map((error, key) => (
                      <li
                        key={key}
                        className={`list__item--${errorLevel} fs-n2 lh-md`}
                      >
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            <ul className="list--order-data list--no-style">
              <EditDimensions />
              <EditWeight />
              <PackagingWeight />
              <RequestedShippingMethod method={requestedShippingMethod} />
              <ShipFrom label="Shipping From" orderNumber={orderNumber} />
              <AssignedWarehouse orderNumber={orderNumber} />
              {!shippingMethodName && <InsuranceItem />}
              <PreconfiguredPackingList orderNumber={orderNumber} />
            </ul>

            {shippingMethodName && (
              <div className="list__item--order-data list__item--shipping-info">
                <ul className="list--order-data list--no-style">
                  <LabelItem label="Carrier">{shipperName}</LabelItem>
                  <LabelItem label="Shipping Method">
                    {shippingMethodName}
                  </LabelItem>
                  <PackageType boxShape={boxShape} />
                  <InsuranceItem />
                  {!isNil(cost) && (
                    <LabelItem label="Estimated Cost">
                      <FormattedRateCost
                        cost={cost}
                        currencySymbol={shipperCurrencySymbol}
                      />
                    </LabelItem>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
        {!isCancelled && parentView !== 'OrderDetail' && (
          <ListItem className="margin-top-5">
            <ButtonLink onClick={showShippingLabelTab}>
              Create a shipping label
            </ButtonLink>
          </ListItem>
        )}
      </div>
    </LabelConfigContext.Provider>
  )
}
